// floatingMenuActions.js
// Action handlers for the floating menu

import {addMetaMark} from "../../helpers/metaMarksHelpers";
import {initializeReferenceMark} from "../../yMaps/referencesYMap";
import useYDocStore from "../../../../stores/useYDocStore";
import {setActiveMarginMarker} from "../marginMarkersPlugin";
import {applyLink} from "../../components/toolbar/toolbarUtils";
import {getExpertDescription} from "../../helpers/getExpertDescription";
import {getLinkRangeAtCursor, safeOpenNewTab, updateURLParams} from "./floatingMenuUtils";
import useExpertStore from "../../../../stores/useExpertStore";
import {useReportStore} from "../../../../features/products/useReportStore";
import {getTopLevelBlockAtPos} from "../modules/blockUtils";

/**
 * Handle link entity action - opens entity search dialog
 * @param {Object} view - ProseMirror editor view
 */
export function handleLinkEntity(view) {
    const event = new CustomEvent('openEntitySearch', {
        detail: {view}
    });
    window.dispatchEvent(event);
}

/**
 * Handle find expert action
 * @param {Object} view - ProseMirror editor view
 * @param {number} from - Selection start position
 * @param {number} to - Selection end position
 * @param {Function} setLoadingState - Callback to set loading state
 * @param {Function} hideMenu - Callback to hide menu
 */
export async function handleFindExpert(view, from, to, setLoadingState, hideMenu) {
    const {state} = view;
    const text = state.doc.textBetween(from, to, "\n");

    if (!text || text.trim().length === 0) {
        hideMenu();
        return;
    }

    try {
        // Set loading state
        setLoadingState('findExpert');

        // Get expert description from LLM
        const expertDescription = await getExpertDescription(state, text);

        // Clear loading state
        setLoadingState(null);

        // Navigate to find expert panel using Zustand
        useReportStore.getState().setLeftPanelTab('aiAssistant');
        useReportStore.getState().setAiView('find_expert');
        updateURLParams({
            expertTab: "ai_suggest",
        });

        // Trigger expert generation via Zustand store
        useExpertStore.getState().setInstructionsAndGenerate(expertDescription);

        hideMenu();
    } catch (error) {
        console.error('Error getting expert description:', error);

        // Reset loading state
        setLoadingState(null);

        // Navigate to find expert panel using Zustand
        useReportStore.getState().setLeftPanelTab('aiAssistant');
        useReportStore.getState().setAiView('find_expert');
        updateURLParams({
            expertTab: "ai_suggest",
        });

        // Fall back to using selected text as instructions
        useExpertStore.getState().setInstructionsAndGenerate(text);

        hideMenu();
    }
}

/**
 * Handle more examples action
 * @param {Object} view - ProseMirror editor view
 * @param {number} from - Selection start position
 * @param {number} to - Selection end position
 */
export function handleMoreExamples(view, from, to) {
    const {state} = view;
    const {doc} = state;
    const text = doc.textBetween(from, to, "\n");

    if (!text || text.trim().length === 0) {
        return;
    }

    const normalizeWs = (s) => s.replace(/\s+/g, ' ').trim();
    const selectionNorm = normalizeWs(text);

    const startBlock = getTopLevelBlockAtPos(doc, from);
    const endBlock = getTopLevelBlockAtPos(doc, to);
    let passage = '';
    if (startBlock && endBlock) {
        const ctxFrom = Math.min(startBlock.from, endBlock.from);
        const ctxTo = Math.max(startBlock.to, endBlock.to);
        passage = doc.textBetween(ctxFrom, ctxTo, "\n");
    }

    const store = useReportStore.getState();
    store.setQuery(text);
    store.setAiSearch(true);
    if (passage && normalizeWs(passage).includes(selectionNorm) && passage.length > text.length) {
        store.setSearchPassageContext(passage);
    } else {
        store.setSearchPassageContext('');
    }
    store.setLeftPanelTab('search');
}

/**
 * Handle link context actions (open, edit, remove)
 * @param {Object} view - ProseMirror editor view
 * @param {string} actionType - Type of link action
 * @param {Object} linkContext - Link context object
 */
export function handleLinkAction(view, actionType, linkContext) {
    const {state} = view;
    const linkCtx = linkContext || getLinkRangeAtCursor(state);

    if (!linkCtx) return;

    switch (actionType) {
        case "openLink":
            safeOpenNewTab(linkCtx.attrs?.href);
            break;

        case "editLink":
            const currentLink = linkCtx.attrs?.href || "https://";
            const href = window.prompt("Enter URL", currentLink);
            if (href) {
                applyLink(view.state, view.dispatch, href);
            }
            break;

        case "removeLink":
            const linkMark = state.schema.marks.link;
            if (linkMark) {
                const tr = state.tr.removeMark(linkCtx.from, linkCtx.to, linkMark);
                view.dispatch(tr.scrollIntoView());
            }
            break;

        default:
            break;
    }
}

/**
 * Handle meta mark actions (comment, reference)
 * @param {Object} view - ProseMirror editor view
 * @param {string} actionType - Type of meta mark
 * @returns {string|null} UID of created mark
 */
export function handleMetaMarkAction(view, actionType) {
    // Add the ProseMirror mark. For reference marks, the Y.Map entry under
    // Y.Map("referenceMarks") is initialised lazily when the first reference
    // is added via addReference() — no Firestore write needed.
    //
    // For reference marks we wrap the ProseMirror dispatch and the Y.Map
    // initialization inside a single ydoc.transact() so that Y.UndoManager
    // captures them as one atomic undo item.

    let uid;

    if (actionType === 'reference') {
        const {ydoc, yReferenceMarks} = useYDocStore.getState();

        const doCreate = () => {
            uid = addMetaMark(view, actionType);
            if (uid && ydoc && yReferenceMarks) {
                initializeReferenceMark(ydoc, yReferenceMarks, uid);
            } else if (uid && (!ydoc || !yReferenceMarks)) {
                console.warn('[handleMetaMarkAction] Y.Doc not ready — reference mark not written to yReferenceMarks');
            }
        };

        if (ydoc) {
            ydoc.transact(doCreate, null);
        } else {
            doCreate();
        }
    } else {
        uid = addMetaMark(view, actionType);
    }

    if (uid) {
        console.log(`Added ${actionType} mark with UID:`, uid);
        // Activate the newly created mark in the margin rail.
        // For comments, pass created:true so CommentMarker auto-focuses the input.
        setActiveMarginMarker(view, {
            type: actionType,
            uid,
            ...(actionType === 'comment' ? {created: true} : {}),
        });
    }

    return uid;
}
